import type { Vector } from "@common/utils/vector";
import type { FillStyle, Graphics } from "pixi.js";
import { shoreDetails, shoreOffset, shoreRibbons, shorelineRuns, shoreVariation, type ShorePoint } from "./shorelineGeometry";

/** Fixed ground art, built at map setup. No ticker, particles, filters or additional textures. */
export function coastalShore(ctx: Graphics, points: readonly Vector[], scale: number, water: boolean, river = false,
    waterAt?: (point: Vector) => boolean, grassFill?: FillStyle): void {
    const runs = shorelineRuns(points, river, water ? waterAt : undefined, 4);
    const detailed = scale > 1;
    const fill = (polygons: readonly Vector[][], style: FillStyle, rounded = false): void => {
        ctx.beginPath();
        for (const polygon of polygons) {
            if (!rounded) { ctx.poly(polygon.flatMap(p => [p.x * scale, p.y * scale])); continue; }
            const first = polygon[0], last = polygon[polygon.length - 1];
            ctx.moveTo((first.x + last.x) * scale / 2, (first.y + last.y) * scale / 2);
            for (let i = 0; i < polygon.length; i++) {
                const p = polygon[i], next = polygon[(i + 1) % polygon.length];
                ctx.quadraticCurveTo(p.x * scale, p.y * scale,
                    (p.x + next.x) * scale / 2, (p.y + next.y) * scale / 2);
            }
            ctx.closePath();
        }
        if (polygons.length) ctx.fill(style);
    };
    const ribbon = (inner: (p: ShorePoint) => number, outer: (p: ShorePoint) => number,
        style: FillStyle, sideAt?: (point: Vector) => boolean): void => {
        fill(runs.flatMap(run => shoreRibbons(run, inner, outer, sideAt)), style);
    };

    if (!water) {
        // A narrow earthy lip and textured fingers interrupt the former straight beige band.
        const edge = (p: ShorePoint): number => 0.8 + shoreVariation(p) * 4.6;
        ribbon(() => -2.8, p => edge(p) + 1.8, { color: 0x756348, alpha: 0.12 });
        ribbon(p => edge(p) - 0.2, p => edge(p) + 0.85, { color: 0x5c6040, alpha: 0.16 });
        ribbon(() => -3.5, edge, grassFill ?? { color: 0x668843 });
        // Baked top-left light gives the bank a small raised lip without a normal map or lights.
        ribbon(p => edge(p) - (0.3 + Math.max(0, -p.nx * 0.7 - p.ny * 0.7) * 0.8),
            edge, { color: 0xc2cd8c, alpha: 0.23 });
        if (!detailed) return;
        const tufts = shoreDetails(runs, river ? 160 : 420, 2);
        const leaves: Vector[][][] = [[], [], []], patches: Vector[][] = [], grains: Vector[][] = [];
        for (const p of tufts) {
            const variation = shoreVariation(p, 4);
            if (variation < 0.25) continue;
            const offset = 1.2 + variation * 5;
            const local = (along: number, outward: number): Vector => ({
                x: p.x - p.ny * along + p.nx * outward,
                y: p.y + p.nx * along + p.ny * outward
            });
            const size = 0.45 + shoreVariation(p, 9) * 0.75;
            patches.push([local(-size, offset - 1.8), local(-size * 1.4, offset),
                local(-size * 0.6, offset + 0.7), local(size * 0.3, offset + 1.1),
                local(size * 1.3, offset + 0.7), local(size, offset - 1.8)]);
            if (variation > 0.65) grains.push([local(2.8, offset + 3.4), local(3.2, offset + 3.8),
                local(3.7, offset + 3.5), local(3.2, offset + 3.2)]);
            // Three small tapered leaves, all flat painted silhouettes on the ground layer.
            for (let leaf = 0; leaf < 3; leaf++) {
                const spread = leaf - 1, length = 1.6 + shoreVariation(p, leaf + 8) * 1.6;
                leaves[leaf].push([local(spread * 0.55 - 0.42, offset - 1),
                    local(spread * 1.8, offset + length), local(spread * 0.55 + 0.42, offset - 1)]);
            }
        }
        fill(patches.map(poly => poly.map(p => ({ x: p.x + 0.45, y: p.y + 0.65 }))),
            { color: 0x6b6041, alpha: 0.2 }, true);
        fill(patches, grassFill ?? { color: 0x668843 }, true);
        fill(grains, { color: 0x867a59, alpha: 0.55 });
        for (const [i, color] of [0x799440, 0x91a54d, 0x648537].entries()) fill(leaves[i], { color, alpha: 0.85 });
        fill(leaves[1].map(leaf => [leaf[0], leaf[1], {
            x: (leaf[0].x + leaf[2].x) / 2, y: (leaf[0].y + leaf[2].y) / 2
        }]), { color: 0xc7ce80, alpha: 0.4 });
        return;
    }

    const landAt = waterAt ? (p: Vector): boolean => !waterAt(p) : undefined;
    // Wet sand follows the dry side, while uneven turquoise shelves remain inside the water.
    ribbon(p => -5 - shoreVariation(p, 7) * 2.4, () => -0.2, { color: 0x96896d, alpha: 0.07 }, landAt);
    ribbon(p => -2.4 - shoreVariation(p, 5), () => -0.2, { color: 0xc5c79b, alpha: 0.18 }, landAt);
    for (const [width, alpha, color] of [[river ? 10 : 24, 0.035, 0x4bd3ca], [river ? 8 : 19, 0.06, 0x6edfd1],
        [river ? 6 : 14, 0.1, 0x88e6d3], [river ? 4.5 : 9, 0.16, 0xa6e9d1], [4, 0.22, 0xc0ecd0]] as const) {
        ribbon(() => 0.25, p => width * (0.72 + shoreVariation(p, width) * 0.38),
            { color, alpha }, waterAt);
    }
    ribbon(() => 0.25, p => 1.2 + shoreVariation(p, 3) * 1.2, { color: 0xe5f2cd, alpha: 0.25 }, waterAt);

    const foam: Vector[][] = [], lace: Vector[][] = [];
    const candidates = runs.flatMap(run => [0, 1].flatMap(row => {
        const fragments: { points: ShorePoint[]; row: number }[] = [];
        for (let start = row * 3; start + 3 < run.length; start += 7) {
            fragments.push({ points: run.slice(start, start + 6), row });
        }
        return fragments;
    }));
    const count = Math.min(detailed ? (river ? 220 : 420) : 140, candidates.length);
    for (let index = 0; index < count; index++) {
        const { points: wave, row } = candidates[Math.floor(index * candidates.length / count)];
        const variation = shoreVariation(wave[0], 11);
        if (variation < (row ? 0.28 : 0.1)) continue;
        const crest = (p: ShorePoint, i: number): number => (row ? 6.4 : 1.6)
            + Math.sin(i * 1.4 + variation * 6) * (row ? 1.4 : 0.9);
        const width = (p: ShorePoint, i: number): number => {
            const taper = Math.sin(Math.PI * i / (wave.length - 1));
            return (row ? 0.22 : 0.32) + taper * (row ? 0.7 : 1.15) * (0.5 + shoreVariation(p, 1));
        };
        (row ? lace : foam).push(...shoreRibbons(wave, crest, (p, i) => crest(p, i) + width(p, i), waterAt));
        if (!detailed || row) continue;
        for (let i = 1; i < wave.length - 1; i += 2) {
            const p = wave[i], offset = crest(p, i) + width(p, i);
            const a = shoreOffset(p, offset, waterAt), b = shoreOffset(p, offset + 1.1, waterAt);
            if (!a || !b) continue;
            const tip = { x: a.x - p.ny * 1.4, y: a.y + p.nx * 1.4 };
            if (waterAt && !waterAt(tip)) continue;
            foam.push([a, b, tip]);
        }
    }
    fill(lace, { color: 0xf0ffef, alpha: 0.62 }, true);
    fill(foam, { color: 0xf7fff1, alpha: 0.92 }, true);
}
